import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Popover,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { RobotoTooltip } from "@/shared/components";
import { usePopupState } from "@/shared/hooks/usePopupState";

export interface FilterOption {
  label: string;
  value: string;
}

interface FilterDropdownProps {
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  buttonTitle: string;
  title?: string;
  buttonIcon?: React.ReactNode;
  sx?: SxProps;
  testId?: string;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  buttonTitle,
  title,
  buttonIcon = <FilterListIcon />,
  sx,
  testId,
}) => {
  const theme = useTheme();
  const popupState = usePopupState();

  const handleOptionToggle = React.useCallback(
    (value: string) => {
      const isSelected = selectedValues.includes(value);
      if (isSelected) {
        onChange(selectedValues.filter((v) => v !== value));
      } else {
        onChange([...selectedValues, value]);
      }
    },
    [selectedValues, onChange],
  );

  const handleSelectAll = React.useCallback(() => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((option) => option.value));
    }
  }, [selectedValues.length, options, onChange]);

  const handleClearAll = React.useCallback(() => {
    onChange([]);
  }, [onChange]);

  const allSelected = selectedValues.length === options.length;
  const someSelected = selectedValues.length > 0;
  const indeterminate = someSelected && !allSelected;

  const buttonText = someSelected
    ? `${buttonTitle} (${selectedValues.length})`
    : buttonTitle;

  return (
    <Box display="flex" alignItems="center" gap={theme.spacing(1)} sx={sx}>
      <Button
        variant="outlined"
        size="small"
        startIcon={buttonIcon}
        endIcon={<ExpandMoreIcon />}
        onClick={(event) => popupState.open({ anchorEl: event.currentTarget })}
        data-testid={testId}
        sx={{
          minWidth: 120,
          justifyContent: "space-between",
          textTransform: "none",
          color: someSelected ? "primary.main" : "text.secondary",
          borderColor: someSelected ? "primary.main" : "divider",
        }}
      >
        {buttonText}
      </Button>

      {someSelected && (
        <RobotoTooltip title={`Clear ${buttonTitle.toLowerCase()} filters`}>
          <IconButton
            onClick={handleClearAll}
            color="primary"
            size="small"
            data-testid={`clear-${testId}-filters`}
          >
            <HighlightOffIcon />
          </IconButton>
        </RobotoTooltip>
      )}

      <Popover
        open={popupState.isOpen}
        anchorEl={popupState.anchor}
        onClose={popupState.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        marginThreshold={5}
        data-testid={`${testId}-popover`}
      >
        <Paper
          elevation={8}
          sx={{
            minWidth: 250,
            maxWidth: 300,
            padding: theme.spacing(1.5),
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {title || `Filter by ${buttonTitle}`}
          </Typography>

          <Divider sx={{ mb: 1 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={indeterminate}
                onChange={handleSelectAll}
                size="small"
                data-testid={`${testId}-select-all`}
              />
            }
            label={
              <Typography variant="body2" fontWeight="medium">
                Select All
              </Typography>
            }
            sx={{ mb: 1, width: "100%" }}
          />

          <Divider sx={{ mb: 1 }} />

          <Stack spacing={0.5}>
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleOptionToggle(option.value)}
                      size="small"
                      data-testid={`${testId}-option-${option.value}`}
                    />
                  }
                  label={
                    <Typography variant="body2">{option.label}</Typography>
                  }
                  sx={{
                    width: "100%",
                    margin: 0,
                    padding: theme.spacing(0.5),
                    "&:hover": {
                      backgroundColor: "action.hover",
                      borderRadius: 1,
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Paper>
      </Popover>
    </Box>
  );
};
